import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout/Layout";
import "./Cart.css";
import { useCartPageData } from "../../hooks/api/useCartPageData";
import { useCart } from "../../hooks/logic/useCart";
import { useProductsByIds } from "../../hooks/api/useProductsByIds";
import Button from "../../components/Buttons";
import { Summary } from "../../types/carts";
import ProductList from "./ProductList/ProductList";

const SERVICES_PRICE = 100;
const ONLINE_PAYMENT_DISCOUNT = 0.10;

const Cart: React.FC = () => {
  const [showCartOrderPage, setShowCartOrderPage] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<number>();
  const [selectedDelivery, setSelectedDelivery] = useState<number>();
  const [recipientInfo, setRecipientInfo] = useState({
    lastName: "",
    firstName: "",
    middleName: "",
    phone: "",
    deliveryAddress: "",
  });

  const { handleUpdateOrRemoveProduct, products: cartProducts, submitOrder } = useCart();
  const ids = useMemo(() => cartProducts.map(({ id }) => id), [cartProducts]);
  const { data: cartPageData } = useCartPageData();
  const { data: products } = useProductsByIds(ids);

  // Дефолтний вибір першого платежу
  useEffect(() => {
    if (!cartPageData || selectedPayment) return;
    const { payment } = cartPageData.data.attributes.cart_order_view;
    const first = payment.payments.data[0];
    if (first) setSelectedPayment(first.id);
  }, [cartPageData]);

  const isOnlinePayment = useMemo(() => {
    if (!cartPageData || !selectedPayment) return false;
    const { payment } = cartPageData.data.attributes.cart_order_view;
    const selected = payment.payments.data.find(({ id }) => id === selectedPayment);
    return (
      selected?.attributes?.type === "online" ||
      selected?.attributes?.label?.toLowerCase().includes("онлайн")
    );
  }, [cartPageData, selectedPayment]);

  const calculateTotals = useCallback(() => {
  if (!products || !cartProducts.length)
    return {
      productPrice: 0,
      servicesPrice: 0,
      rawTotal: 0,
      delivery: 150,
      discount: 0,
      fullTotal: 0,
      includeDelivery: false,
    };

  const rawTotal = cartProducts.reduce((sum, cartProduct) => {
    const product = products.find(({ id }) => String(id) === cartProduct.id);
    return sum + (product ? Number(product.attributes.price) * cartProduct.count : 0);
  }, 0);

  // Послуги = 100грн * кількість унікальних товарів (або штук)
  const totalItems = cartProducts.reduce((sum, p) => sum + p.count, 0);
  const servicesPrice = SERVICES_PRICE * totalItems;

  const productPrice = Math.max(0, rawTotal - servicesPrice);
  const delivery = 150;
  const includeDelivery = isOnlinePayment;
  const discount = isOnlinePayment ? Math.round(rawTotal * ONLINE_PAYMENT_DISCOUNT) : 0;
  const fullTotal = rawTotal + (includeDelivery ? delivery : 0) - discount;

  return { productPrice, servicesPrice, rawTotal, delivery, discount, fullTotal, includeDelivery };
}, [products, cartProducts, isOnlinePayment]);

  const canPlaceOrder = useCallback(() => {
    if (!selectedPayment || !selectedDelivery) return false;
    const requiredFields = ["lastName", "firstName", "phone", "deliveryAddress"];
    for (const field of requiredFields) {
      if (!recipientInfo[field as keyof typeof recipientInfo]) return false;
    }
    if (!cartProducts.length) return false;
    return true;
  }, [selectedPayment, selectedDelivery, recipientInfo, cartProducts]);

  const { productPrice, servicesPrice, rawTotal, delivery, discount, fullTotal, includeDelivery } = calculateTotals();

  const handleOrder = useCallback(async () => {
    if (!showCartOrderPage) {
      setShowCartOrderPage(true);
      return;
    }
    if (!canPlaceOrder()) {
      alert("Будь ласка, заповніть всі обов'язкові поля.");
      return;
    }
    const orderData = {
      firstName: recipientInfo.firstName,
      lastName: recipientInfo.lastName,
      middleName: recipientInfo.middleName || "",
      deliveryAddress: recipientInfo.deliveryAddress,
      phone: recipientInfo.phone,
      email: "email@example.com",
      deliveryId: selectedDelivery!,
      paymentId: selectedPayment!,
      products: cartProducts,
      price: rawTotal,
      deliveryPrice: includeDelivery ? delivery : 0,
      totalPrice: fullTotal,
    };
    try {
      await submitOrder(orderData);
      setOrderSuccess(true);
    } catch (error) {
      console.error("Помилка:", error);
      alert("Виникла помилка при створенні замовлення. Спробуйте знову.");
    }
  }, [
    showCartOrderPage, canPlaceOrder, recipientInfo,
    selectedDelivery, selectedPayment, cartProducts,
    rawTotal, delivery, fullTotal, includeDelivery, submitOrder,
  ]);

  const renderRecipientForm = useCallback(() => {
    if (!cartPageData) return null;
    const { recipient } = cartPageData.data.attributes.cart_order_view;
    return (
      <div className="cart__recipient-form">
        <h4 className="cart__sub-title">{recipient.title}</h4>
        <div className="cart__recipient-fields">
          <div className="cart__recipient-field">
            <label htmlFor="lastName">{recipient.lastName_label}</label>
            <input type="text" id="lastName" value={recipientInfo.lastName}
              onChange={(e) => setRecipientInfo({ ...recipientInfo, lastName: e.target.value })} />
          </div>
          <div className="cart__recipient-field">
            <label htmlFor="firstName">{recipient.firstName_label}</label>
            <input type="text" id="firstName" value={recipientInfo.firstName}
              onChange={(e) => setRecipientInfo({ ...recipientInfo, firstName: e.target.value })} />
          </div>
          <div className="cart__recipient-field">
            <label htmlFor="middleName">{recipient.middleName_label}</label>
            <input type="text" id="middleName" value={recipientInfo.middleName}
              onChange={(e) => setRecipientInfo({ ...recipientInfo, middleName: e.target.value })} />
          </div>
          <div className="cart__recipient-field">
            <label htmlFor="phone">{recipient.phone_label}</label>
            <input type="text" id="phone" value={recipientInfo.phone}
              onChange={(e) => setRecipientInfo({ ...recipientInfo, phone: e.target.value })} />
          </div>
        </div>
      </div>
    );
  }, [cartPageData, recipientInfo]);

  const renderPaymentForm = useCallback(() => {
    if (!cartPageData) return null;
    const { payment } = cartPageData.data.attributes.cart_order_view;
    return (
      <div className="cart__payment-form">
        <h4 className="cart__sub-title">{payment.title}</h4>
        <div className="cart__payment-options">
          {payment.payments.data.map(({ id, attributes: { label, description } }) => (
            <div key={id} className="cart__payment-option" onClick={() => setSelectedPayment(id)}>
              <div className={selectedPayment === id
                ? "cart__payment-option-selector cart__payment-option-selector_selected"
                : "cart__payment-option-selector"} />
              <label>
                <strong>{label}</strong>
                <span>{description}</span>
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }, [cartPageData, selectedPayment]);

  const renderDeliveryForm = useCallback(() => {
    if (!cartPageData) return null;
    const { delivery } = cartPageData.data.attributes.cart_order_view;
    return (
      <div className="cart__payment-form">
        <h4 className="cart__sub-title">{delivery.title}</h4>
        <div className="cart__payment-options">
          {delivery.deliveries.data.map(({ id, attributes: { label, description } }) => (
            <div key={id} className="cart__payment-option" onClick={() => setSelectedDelivery(id)}>
              <div className={selectedDelivery === id
                ? "cart__payment-option-selector cart__payment-option-selector_selected"
                : "cart__payment-option-selector"} />
              <label>
                <strong>{label}</strong>
                <span>{description ?? " "}</span>
              </label>
            </div>
          ))}
          <div className="cart__recipient-field">
            <label htmlFor="deliveryAddress">{delivery.deliveryAddress_label}</label>
            <input type="text" id="deliveryAddress" value={recipientInfo.deliveryAddress}
              onChange={(e) => setRecipientInfo({ ...recipientInfo, deliveryAddress: e.target.value })} />
          </div>
        </div>
      </div>
    );
  }, [selectedDelivery, cartPageData, recipientInfo]);

  const renderSummary = useCallback(
    (summary: Summary, currencyName: string) => (
      <div className="cart__summary">
        <h4 className="cart__summary-title">{summary.section_title}</h4>

        {/* Ціна товарів */}
        <div className="cart__summary-item">
          <h6 className="cart__summary-item-title">{summary.price_label}</h6>
          <p className="cart__summary-item-text">{productPrice} {currencyName}</p>
        </div>

        {/* Послуги */}
        <div className="cart__summary-item cart__summary-item--services">
          <h6 className="cart__summary-item-title">Послуги</h6>
          <p className="cart__summary-item-text">{servicesPrice} {currencyName}</p>
        </div>

        {/* Доставка — тільки при онлайн-платежі */}
        {includeDelivery && (
          <div className="cart__summary-item">
            <h6 className="cart__summary-item-title">{summary.deliveryPrice_label}</h6>
            <p className="cart__summary-item-text">{delivery} {currencyName}</p>
          </div>
        )}

        {/* Знижка 5% при онлайн-платежі */}
        {isOnlinePayment && discount > 0 && (
          <div className="cart__summary-item cart__summary-item--discount">
            <h6 className="cart__summary-item-title">Знижка (10%)</h6>
            <p className="cart__summary-item-text cart__summary-item-text--discount">
              -{discount} {currencyName}
            </p>
          </div>
        )}

        <div className="cart__summary-line" />

        {/* Загальна сума */}
        <div className="cart__summary-item">
          <h6 className="cart__summary-item-title">{summary.fullPrice_label}</h6>
          <p className="cart__summary-item-text cart__summary-item-text--total">
            {fullTotal} {currencyName}
          </p>
        </div>

        <Button
          buttonProps={summary.pay_button}
          onClick={handleOrder}
          disabled={showCartOrderPage && !canPlaceOrder()}
        />
      </div>
    ),
    [productPrice, servicesPrice, delivery, discount, fullTotal,
     isOnlinePayment, includeDelivery, handleOrder, canPlaceOrder, showCartOrderPage]
  );

  const renderContent = useCallback(() => {
    if (!cartPageData) return null;
    const { cart_order_view, summary, cart_view, currency } = cartPageData.data.attributes;
    const currencyName = currency?.data?.attributes?.name || "";

    if (showCartOrderPage) {
      return (
        <div>
          <h4 className="title cart__title">{cart_order_view.title}</h4>
          <div className="cart__hero">
            <div className="cart__products">
              {renderDeliveryForm()}
              {renderRecipientForm()}
              {renderPaymentForm()}
            </div>
            <div className="cart__summary-container">
              <div className="cart__summary-products">
                <ProductList products={products} cartProducts={cartProducts}
                  currencyName={currencyName}
                  handleUpdateOrRemoveProduct={handleUpdateOrRemoveProduct} size="small" />
              </div>
              {renderSummary(summary, currencyName)}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <h4 className="title cart__title">{cart_view.title}</h4>
        <div className="cart__hero">
          <div className="cart__products">
            <ProductList products={products} cartProducts={cartProducts}
              currencyName={currencyName}
              handleUpdateOrRemoveProduct={handleUpdateOrRemoveProduct} size="large" />
          </div>
          <div className="cart__summary-container">
            {renderSummary(summary, currencyName)}
          </div>
        </div>
      </div>
    );
  }, [
    cartPageData, products, renderDeliveryForm, cartProducts,
    showCartOrderPage, renderPaymentForm, handleUpdateOrRemoveProduct,
    renderSummary, renderRecipientForm,
  ]);

  if (!cartPageData) return null;

  const { emptyCartText } = cartPageData.data.attributes;

  if (orderSuccess) {
    return (
      <Layout withFooter={false}>
        <div className="cart__success">
          <div className="cart__success-content">
            <div className="cart__success-icon">
              <svg width="48" height="48" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="1.5"/>
                <path d="M8 12l3 3 5-5" stroke="#22c55e" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="cart__success-title">Замовлення прийнято!</h2>
            <p className="cart__success-text">
              Ваше замовлення зараз в обробці. Менеджер зв'яжеться з вами
              найближчим часом для підтвердження.
            </p>
            <a href="/" className="cart__success-btn">Повернутися на головну</a>
          </div>
        </div>
      </Layout>
    );
  }

  if (!products.length) {
    return (
      <Layout withFooter={false}>
        <div className="container cart__container cart__empty">
          <div className="cart__empty-content">
            <svg width="64" height="64" fill="none" viewBox="0 0 24 24">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"
                stroke="#d0d0d0" strokeWidth="1.5" strokeLinejoin="round"/>
              <line x1="3" y1="6" x2="21" y2="6" stroke="#d0d0d0" strokeWidth="1.5"/>
              <path d="M16 10a4 4 0 01-8 0" stroke="#d0d0d0" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <h3 className="cart__empty-title">{emptyCartText}</h3>
            <p className="cart__empty-subtitle">Додайте товари, щоб оформити замовлення</p>
            <a href="/" className="cart__empty-btn">Перейти до покупок</a>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout withFooter={false}>
      <div className="container cart__container">{renderContent()}</div>
    </Layout>
  );
};

export default Cart;
